import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import Modal from '../../../components/modal';
import Input from '../../../components/input';
import TextArea from '../../../components/textArea';
import Button from '../../../components/button';
import SearchableSelect from '../../../components/searchableSelect';
import Spinner from '../../../components/spinner';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';

import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import { listIpsecPolicies } from '../../../hooks/local/userReducer';
import { showSuccessMessage } from '../../../hooks/constants';

const enumOptions = {
  auth_algorithm: ['sha1', 'sha256', 'sha384', 'sha512'].map((v) => ({
    value: v,
    label: v,
  })),
  encryption_algorithm: ['aes-128', 'aes-192', 'aes-256', '3des'].map((v) => ({
    value: v,
    label: v,
  })),
  pfs: ['group2', 'group5', 'group14'].map((v) => ({ value: v, label: v })),
  units: ['seconds'].map((v) => ({ value: v, label: v })),
  // units: ['seconds', 'kilobytes'].map((v) => ({ value: v, label: v })),
  encapsulation_mode: ['tunnel', 'transport'].map((v) => ({
    value: v,
    label: v,
  })),
  transform_protocol: ['ESP', 'AH', 'AH-ESP'].map((v) => ({
    value: v,
    label: v,
  })),
};

const VpnIpsecPolicies = () => {
  document.title = 'IPSec Policies - VPN';

  const projectId = useSelector((state) => state.user.selectedProject?.id);
  const dispatch = useDispatch();
  const ipsecPolicies = useSelector((state) => state.user.ipsecPolicies);

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 5;
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: '', name: '' });

  const refetch = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      await dispatch(listIpsecPolicies(projectId));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const filtered = useMemo(() => {
    const arr = Array.isArray(ipsecPolicies) ? ipsecPolicies : [];
    const q = searchText.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [ipsecPolicies, searchText]);

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filtered.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filtered.length / entriesPerPage) || 1;

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const createForm = useFormik({
    initialValues: {
      name: '',
      description: '',
      auth_algorithm: 'sha1',
      encryption_algorithm: 'aes-128',
      encapsulation_mode: 'tunnel',
      transform_protocol: 'ESP',
      pfs: 'group5',
      units: 'seconds',
      value: 3600,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!projectId) return;
      setLoading(true);
      try {
        await apiEndPoints.createIpsecPolicy(projectId, values);
        showSuccessMessage('IPSec Policy created');
        setCreateOpen(false);
        resetForm();
        await refetch();
      } finally {
        setLoading(false);
      }
    },
  });

  const editForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: selected?.name || '',
      description: selected?.description || '',
      auth_algorithm: selected?.auth_algorithm || 'sha1',
      encryption_algorithm: selected?.encryption_algorithm || 'aes-128',
      encapsulation_mode: selected?.encapsulation_mode || 'tunnel',
      transform_protocol: selected?.transform_protocol || 'ESP',
      pfs: selected?.pfs || 'group5',
      units: selected?.units || 'seconds',
      value: selected?.value ?? 3600,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
    }),
    onSubmit: async (values) => {
      if (!projectId || !selected?.id) return;
      setLoading(true);
      try {
        await apiEndPoints.updateIpsecPolicy(projectId, selected.id, values);
        showSuccessMessage('IPSec Policy updated');
        setEditOpen(false);
        setSelected(null);
        await refetch();
      } finally {
        setLoading(false);
      }
    },
  });

  const runDelete = async () => {
    if (!projectId || !confirmDelete.id) return;
    setLoading(true);
    try {
      await apiEndPoints.deleteIpsecPolicy(projectId, confirmDelete.id);
      showSuccessMessage('IPSec Policy deleted');
      setConfirmDelete({ open: false, id: '', name: '' });
      await refetch();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 md:px-10 py-6 min-h-screen">
      <Spinner loading={loading} />

      <p className="crimson-font text-xl font-medium text-primaryBlue mb-6">
        IPSec Policies
      </p>

      <div className="grid md:flex md:items-center md:justify-between gap-4 mb-4">
        <div className="w-full md:w-1/2 lg:w-1/3">
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search IPSec policy..."
            className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
          />
        </div>
        <div
          onClick={() => setCreateOpen(true)}
          className="px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue cursor-pointer w-fit"
        >
          Create IPSec policy
        </div>
      </div>

      <div className="overflow-x-auto rounded-md dark:text-white">
        <table className="w-full text-sm text-left overflow-hidden">
          <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] text-sm font-semibold">
            <tr>
              <th className="px-6 py-4 whitespace-nowrap">Name</th>
              <th className="px-6 py-4 whitespace-nowrap">Description</th>
              <th className="px-6 py-4 whitespace-nowrap">Auth</th>
              <th className="px-6 py-4 whitespace-nowrap">Encryption</th>
              <th className="px-6 py-4 whitespace-nowrap">PFS</th>
              <th className="px-6 py-4 whitespace-nowrap w-56"></th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 text-black/50 dark:text-white/50 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] text-sm">
            {(Array.isArray(currentEntries) ? currentEntries : []).map((row) => (
              <tr
                key={row.id || row.uuid || row.name}
                className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
              >
                <td className="text-start py-4 px-6 text-primaryBlue">
                  {row.name || '—'}
                </td>
                <td className="text-start py-4 px-6">
                  {row.description || '—'}
                </td>
                <td className="text-start py-4 px-6">
                  {row.auth_algorithm || '—'}
                </td>
                <td className="text-start py-4 px-6">
                  {row.encryption_algorithm || '—'}
                </td>
                <td className="text-start py-4 px-6">{row.pfs || '—'}</td>
                <td className="text-start py-4 px-6 flex items-center gap-2">
                  <button
                    className="text-xs border rounded-md px-3 py-2 cursor-pointer"
                    onClick={() => {
                      setSelected(row);
                      setEditOpen(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="text-xs border rounded-md px-3 py-2 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20"
                    onClick={() =>
                      setConfirmDelete({
                        open: true,
                        id: row.id || row.uuid,
                        name: row.name || row.id || row.uuid,
                      })
                    }
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!filtered || filtered.length === 0) && (
              <tr>
                <td className="px-6 py-6 text-sm opacity-70" colSpan={6}>
                  No IPSec policies found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
            currentPage === 1 ? 'opacity-50' : 'opacity-100'
          }`}
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-sm text-primaryBlue dark:text-white">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
            currentPage === totalPages ? 'opacity-50' : 'opacity-100'
          }`}
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      <Modal
        title="Create IPSec Policy"
        isVisible={createOpen}
        onClose={() => setCreateOpen(false)}
        size="md:w-[45%]"
      >
        <form className="grid gap-4" onSubmit={createForm.handleSubmit}>
          <Input
            label="Name"
            name="name"
            value={createForm.values.name}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            onError={createForm.touched.name ? createForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the IPsec policy."
          />
          <Input
            label="Description"
            name="description"
            value={createForm.values.description}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            isLabelIcon={true}
            iconTitle="A description for the IPsec policy."
          />
          <SearchableSelect
            label="Auth Algorithm"
            name="auth_algorithm"
            value={createForm.values.auth_algorithm}
            onChange={createForm.handleChange}
            options={enumOptions.auth_algorithm}
            isLabelIcon={true}
            iconTitle="Valid algorithms are sha1, sha256, sha384 and sha512."
          />
          <SearchableSelect
            label="Encryption Algorithm"
            name="encryption_algorithm"
            value={createForm.values.encryption_algorithm}
            onChange={createForm.handleChange}
            options={enumOptions.encryption_algorithm}
            isLabelIcon={true}
            iconTitle="Valid algorithms are 3des, aes-128, aes-192 and aes-256."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect
              label="Encapsulation Mode"
              name="encapsulation_mode"
              value={createForm.values.encapsulation_mode}
              onChange={createForm.handleChange}
              options={enumOptions.encapsulation_mode}
              isLabelIcon={true}
              iconTitle="The type of IPsec tunnel (tunnel/transport) to be used."
            />
            <SearchableSelect
              label="Transform Protocol"
              name="transform_protocol"
              value={createForm.values.transform_protocol}
              onChange={createForm.handleChange}
              options={enumOptions.transform_protocol}
              isLabelIcon={true}
              iconTitle="The type of protocol (esp, ah, ah-esp) used in IPsec policy."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect
              label="PFS"
              name="pfs"
              value={createForm.values.pfs}
              onChange={createForm.handleChange}
              options={enumOptions.pfs}
              isLabelIcon={true}
              iconTitle="PFS limited to using Diffie-Hellman groups 2, 5 (default) and 14."
            />
            <SearchableSelect
              label="Lifetime Units"
              name="units"
              value={createForm.values.units}
              onChange={createForm.handleChange}
              options={enumOptions.units}
              isLabelIcon={true}
              iconTitle="Life time consists of units and value. Units in 'seconds' and the default value is 3600."
            />
          </div>
          <Input
            label="Lifetime Value"
            name="value"
            type="number"
            value={createForm.values.value}
            onChange={createForm.handleChange}
            onBlur={createForm.handleBlur}
            isLabelIcon={true}
            iconTitle="Life time consists of units and value. Units in 'seconds' and the default value is 3600."
          />
          <div className="flex justify-end gap-2">
            <Button buttonText="Create" buttonRole="submit" />
          </div>
        </form>
      </Modal>

      <Modal
        title="Edit IPSec Policy"
        isVisible={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelected(null);
        }}
        size="md:w-[45%]"
      >
        <form className="grid gap-4" onSubmit={editForm.handleSubmit}>
          <Input
            label="Name"
            name="name"
            value={editForm.values.name}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            onError={editForm.touched.name ? editForm.errors.name : null}
            isLabelIcon={true}
            iconTitle="The name of the IPsec policy."
          />
          <Input
            label="Description"
            name="description"
            value={editForm.values.description}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            isLabelIcon={true}
            iconTitle="A description for the IPsec policy."
          />
          <SearchableSelect
            label="Auth Algorithm"
            name="auth_algorithm"
            value={editForm.values.auth_algorithm}
            onChange={editForm.handleChange}
            options={enumOptions.auth_algorithm}
            isLabelIcon={true}
            iconTitle="Valid algorithms are sha1, sha256, sha384 and sha512."
          />
          <SearchableSelect
            label="Encryption Algorithm"
            name="encryption_algorithm"
            value={editForm.values.encryption_algorithm}
            onChange={editForm.handleChange}
            options={enumOptions.encryption_algorithm}
            isLabelIcon={true}
            iconTitle="Valid algorithms are 3des, aes-128, aes-192 and aes-256."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect
              label="Encapsulation Mode"
              name="encapsulation_mode"
              value={editForm.values.encapsulation_mode}
              onChange={editForm.handleChange}
              options={enumOptions.encapsulation_mode}
              isLabelIcon={true}
              iconTitle="The type of IPsec tunnel (tunnel/transport) to be used."
            />
            <SearchableSelect
              label="Transform Protocol"
              name="transform_protocol"
              value={editForm.values.transform_protocol}
              onChange={editForm.handleChange}
              options={enumOptions.transform_protocol}
              isLabelIcon={true}
              iconTitle="The type of protocol (esp, ah, ah-esp) used in IPsec policy."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchableSelect
              label="PFS"
              name="pfs"
              value={editForm.values.pfs}
              onChange={editForm.handleChange}
              options={enumOptions.pfs}
              isLabelIcon={true}
              iconTitle="PFS limited to using Diffie-Hellman groups 2, 5 (default) and 14."
            />
            <SearchableSelect
              label="Lifetime Units"
              name="units"
              value={editForm.values.units}
              onChange={editForm.handleChange}
              options={enumOptions.units}
              isLabelIcon={true}
              iconTitle="Life time consists of units and value. Units in 'seconds' and the default value is 3600."
            />
          </div>
          <Input
            label="Lifetime Value"
            name="value"
            type="number"
            value={editForm.values.value}
            onChange={editForm.handleChange}
            onBlur={editForm.handleBlur}
            isLabelIcon={true}
            iconTitle="Life time consists of units and value. Units in 'seconds' and the default value is 3600."
          />
          <div className="flex justify-end gap-2">
            <Button buttonText="Update" buttonRole="submit" />
          </div>
        </form>
      </Modal>

      <ConfirmDeleteModal
        open={confirmDelete.open}
        name={confirmDelete.name}
        title="Delete IPSec Policy"
        description="Type the name exactly to confirm deletion"
        onClose={() => setConfirmDelete({ open: false, id: '', name: '' })}
        onConfirm={runDelete}
        isDeleting={loading}
      />
    </div>
  );
};

export default VpnIpsecPolicies;


